import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { CRAFTS_MAP } from "../maps";
import { CHARACTERS, CRAFTS } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";

/**
 * Crafts (S-Craft / regular Craft) guessing mode — 6 guesses to identify the character who uses a given craft.
 * Progressive hints: gender after 2 wrong guesses, debut game after 4 wrong guesses.
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, guessedCrafts: Array<Object>, setGuessedCrafts: (g: Array<Object>) => void }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<Object>} props.guessedCrafts - Previously submitted craft guesses.
 * @param {(g: Array<Object>) => void} props.setGuessedCrafts - Guess state setter.
 * @returns {JSX.Element} The crafts game UI.
 */
export default function CraftsMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  guessedCrafts,
  setGuessedCrafts,
}) {
  const [currentGuess, setCurrentGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection-crafts",
    []
  );
  const [isCopied, setIsCopied] = useState(false);

  const [targetCraft] = useState(
    getDailyItem(CRAFTS, targetDateObj, CRAFTS_MAP)
  );

  const MAX_GUESSES = 6;
  const wrongGuessesCount = guessedCrafts.filter(
    (craft) => craft.character !== targetCraft.character
  ).length;
  const hasWon = guessedCrafts.some(
    (craft) => craft.character === targetCraft.character
  );
  const guessesLeft = MAX_GUESSES - guessedCrafts.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  /** Processes a craft guess — checks win, updates stats, adds to collection. */
  const handleGuess = (selectedCraft) => {
    if (hasWon || isGameOver) return;
    if (guessedCrafts.find((c) => c.character === selectedCraft.character))
      return;

    const newGuesses = [...guessedCrafts, selectedCraft];
    const isWin = selectedCraft.character === targetCraft.character;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    if (!isArchive && (isWin || isLoss)) {
      updateGameStats("crafts", isWin, newGuesses.length, MAX_GUESSES);
    }

    if (isWin) {
      const targetId = String(targetCraft.id);
      if (!collection.some((id) => String(id) === targetId)) {
        setCollection([...collection, targetId]);
      }
    }
    setGuessedCrafts(newGuesses);
    setCurrentGuess("");
    setSuggestions([]);
  };

  const { focusedIndex, setFocusedIndex, handleKeyDown, listRef } =
    useKeyboardNavigation(suggestions, handleGuess);

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-crafts-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));
    }
  }, [hasWon, targetDateStr]);

  const [isGenderRevealed, setIsGenderRevealed] = useState(false);
  const [isGameRevealed, setIsGameRevealed] = useState(false);
  const guessesNeededForGender = Math.max(0, 2 - wrongGuessesCount);
  const guessesNeededForGame = Math.max(0, 4 - wrongGuessesCount);

  const [globalStats, setGlobalStats] = useState(null);

  /** Filters crafts by character name, deduplicates, and sorts by match priority. */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentGuess(value);
    setFocusedIndex(-1);
    if (value.length > 0) {
      const query = value.toLowerCase().trim();
      const seen = new Set();
      const filteredCrafts = CRAFTS.filter((craft) => {
        const match = (craft.character || "").toLowerCase().includes(query);
        if (!match || seen.has(craft.character)) return false;
        seen.add(craft.character);
        return !guessedCrafts.some((g) => g.character === craft.character);
      }).sort((a, b) => {
        const charA = (a.character || "").toLowerCase();
        const charB = (b.character || "").toLowerCase();
        const aStart = charA.startsWith(query);
        const bStart = charB.startsWith(query);
        if (aStart && !bStart) return -1;
        if (!aStart && bStart) return 1;
        return charA.localeCompare(charB);
      });
      setSuggestions(filteredCrafts.slice(0, 15));
    } else {
      setSuggestions([]);
    }
  };

  const syncInProgress = useRef(false);

  // --- CRAFTS MODE TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-crafts-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if ((hasWon || isGameOver) && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "crafts_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedCrafts.length)
              : increment(0),
            guessTally: {},
          };
          guessedCrafts.forEach((craft) => {
            if (craft && craft.character)
              updateData.guessTally[craft.character] = increment(1);
          });
          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");
          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) setGlobalStats(updatedDoc.data());
        } catch (error) {
          console.error("Firebase Error:", error);
        } finally {
          syncInProgress.current = false;
        }
      };
      updateAndFetchStats();
    }
  }, [hasWon, isGameOver, guessedCrafts.length, targetDateStr]);

  /** Copies crafts-mode results (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedCrafts.length : "X";
    let shareText = `Kisekidle - Crafts (${targetDateStr})\nCharacter guesses: ${score}/${MAX_GUESSES}\n\n`;
    
    if (format === "emoji") {
      const emojis = guessedCrafts
        .map((craft) => (craft.character === targetCraft.character ? "🟩" : "🟥"))
        .join("");
      shareText += emojis;
    } else {
      guessedCrafts.forEach((craft, i) => {
        shareText += `${i + 1}. ${craft.character} ${craft.character === targetCraft.character ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\n\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal crafts-terminal">
      <div className="header-container">
        <h2 className="mode-title">Who uses it?</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
      </div>

      <div className="quote-box">
        "{targetCraft.craftName}"
      </div>

      <div className="hints-container">
        <div className="hint-wrapper">
          <button
            className={`hint-btn ${isGenderRevealed || hasWon || isGameOver
              ? "revealed"
              : guessesNeededForGender <= 0
                ? "unlocked"
                : "locked"
            }`}
            onClick={() => guessesNeededForGender <= 0 && setIsGenderRevealed(true)}
            disabled={
              guessesNeededForGender > 0 || isGenderRevealed || hasWon || isGameOver
            }
          >
            {isGenderRevealed || hasWon || isGameOver
              ? `Gender: ${targetCraft.gender}`
              : "Reveal Gender"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForGender > 0
                ? `Available after ${guessesNeededForGender} more guess${guessesNeededForGender > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>

        <div className="hint-wrapper">
          <button
            className={`hint-btn ${isGameRevealed || hasWon || isGameOver
              ? "revealed"
              : guessesNeededForGame <= 0
                ? "unlocked"
                : "locked"
            }`}
            onClick={() => guessesNeededForGame <= 0 && setIsGameRevealed(true)}
            disabled={
              guessesNeededForGame > 0 || isGameRevealed || hasWon || isGameOver
            }
          >
            {isGameRevealed || hasWon || isGameOver
              ? `Game: ${targetCraft.game}`
              : "Reveal Debut Game"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForGame > 0
                ? `Available after ${guessesNeededForGame} more guess${guessesNeededForGame > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>
      </div>

      {hasWon && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 You got it! 🎉</h3>
          <p>
            It was <strong>{targetCraft.character}</strong> with the craft{" "}
            <strong>{targetCraft.craftName}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The character was <strong>{targetCraft.character}</strong> with the
            craft <strong>{targetCraft.craftName}</strong>.
          </p>
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {!hasWon && !isGameOver && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Search for a character..."
            autoFocus
          />
          <button
            className="submit-button"
            onClick={() =>
              suggestions.length > 0 && handleGuess(suggestions[0])
            }
          >
            Submit Guess
          </button>
          {suggestions.length > 0 && (
            <ul className="suggestions-list" ref={listRef}>
              {suggestions.map((craft, index) => (
                <li
                  key={craft.id}
                  className={`${index === focusedIndex ? "focused " : ""}suggestion-item`}
                  onClick={() => handleGuess(craft)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {(CHARACTERS.find((char) => char.name === craft.character)?.imageUrl ||
                    craft.imageUrl) && (
                      <img
                        src={
                          CHARACTERS.find((char) => char.name === craft.character)
                            ?.imageUrl || craft.imageUrl
                        }
                        alt={craft.character}
                        loading="lazy"
                        className="suggestion-image"
                      />
                    )}
                  <span>
                    <strong>{craft.character}</strong>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ul className="guess-list">
        {guessedCrafts
          .slice()
          .reverse()
          .map((craft, index) => {
            const isCorrect = craft.character === targetCraft.character;
            return (
              <li
                key={index}
                className={`guess-item ${isCorrect ? "correct" : "incorrect"}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {(CHARACTERS.find((char) => char.name === craft.character)?.imageUrl ||
                    craft.imageUrl) && (
                      <img
                        src={
                          CHARACTERS.find((char) => char.name === craft.character)
                            ?.imageUrl || craft.imageUrl
                        }
                        alt={craft.character}
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  <strong>{craft.character}</strong>
                </div>
                <span>{isCorrect ? "🎉 Correct!" : "❌ Incorrect"}</span>
              </li>
            );
          })}
      </ul>
    </div>
  );
}
